import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const MembersContainer = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.cream};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, 
      ${props => props.theme.colors.overlay} 0%,
      transparent 100%
    );
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.title};
  color: ${props => props.theme.colors.black};
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: ${props => props.theme.spacing.xl};
  opacity: 0;
  transform: translateY(30px);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.md};
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.wood},
      transparent
    );
  }
`;

const MemberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.xl};
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: ${props => props.theme.spacing.lg};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const MemberImage = styled.div`
  width: 200px;
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: 50%;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const MemberName = styled.h2`
  font-family: ${props => props.theme.fonts.title};
  color: ${props => props.theme.colors.black};
  font-size: 1.8rem;
  margin: 0;
`;

const MemberRole = styled.h3`
  color: ${props => props.theme.colors.wood};
  font-size: 1.2rem;
  margin: 0;
`;

const MemberDescription = styled.p`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
`;

const TagsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const Tag = styled.span`
  background: ${props => props.theme.colors.wood}20;
  color: ${props => props.theme.colors.wood};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const members = [
  {
    name: "Anne Belliard",
    role: "Compositrice & Interprète",
    description: "Auteure-compositrice-interprète, Anne marie sa voix cristalline aux mélodies folk avec une sensibilité unique. Son approche poétique des textes apporte une dimension profonde à chaque création.",
    tags: ["Chant", "Écriture", "Composition", "Piano"],
    image: "/images/anne.jpg"
  },
  {
    name: "Michel Sanz",
    role: "Compositeur & Guitariste",
    description: "Guitariste passionné avec plus de 20 ans d'expérience dans la composition et les arrangements musicaux. Son style unique fusionne folk traditionnelle et modernité.",
    tags: ["Guitare acoustique", "Arrangements", "Composition", "Production"],
    image: "/images/michel.jpg"
  },
  {
    name: "Franck Chauvier",
    role: "Pianiste",
    description: "Pianiste virtuose formé au conservatoire, Thomas apporte sa touche de jazz et sa maîtrise de l'orchestration. Son expertise enrichit chaque morceau d'arrangements sophistiqués.",
    tags: ["Piano", "Jazz", "Orchestration", "Improvisation"],
    image: "/images/franck.jpg"
  }
];

function Members() {
  const titleRef = useRef(null);
  const membersRef = useRef(null);

  useEffect(() => {
    const title = titleRef.current;

    gsap.fromTo(title,
      { opacity: 0, y: 30 },
      { 
        opacity: 1, 
        y: 0, 
        duration: 1,
        scrollTrigger: {
          trigger: title,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    const memberCards = membersRef.current.children;
    gsap.fromTo(memberCards,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        scrollTrigger: {
          trigger: membersRef.current,
          start: 'top 70%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <MembersContainer>
      <PageTitle ref={titleRef}>Nos Membres</PageTitle>
      <MemberGrid ref={membersRef}>
        {members.map((member, index) => (
          <MemberCard key={index}>
            <MemberImage image={member.image} />
            <MemberInfo>
              <MemberName>{member.name}</MemberName>
              <MemberRole>{member.role}</MemberRole>
              <MemberDescription>{member.description}</MemberDescription>
              <TagsContainer>
                {member.tags.map((tag, tagIndex) => (
                  <Tag key={tagIndex}>{tag}</Tag>
                ))}
              </TagsContainer>
            </MemberInfo>
          </MemberCard>
        ))}
      </MemberGrid>
    </MembersContainer>
  );
}

export default Members;